import React, { useState, useEffect } from 'react';
import ProtectedRoute from '../../routes/protected';
import { useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import { Chip } from 'primereact/chip';
import { Dropdown } from 'primereact/dropdown';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import surveyService from '../../services/data/survey.service';
import companyService from '../../services/data/company.service';
import SurveyPageHeader from './SurveyPageHeader';
import SurveyTriggerSection from './SurveyTriggerSection';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const DOUGHNUT_TRACK = '#e9ecef';
const DOUGHNUT_EMPTY = '#dee2e6';

const indicatorDoughnutOptions = {
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                label(ctx) {
                    const ds = ctx.dataset;
                    const total = ds.data.reduce((a, b) => a + b, 0);
                    if (!total) return '';
                    const pct = ((ctx.parsed / total) * 100).toFixed(1);
                    return `${pct}%`;
                },
            },
        },
    },
};

/** Anel de progresso 0..max (scores agregados, típico 0–10). */
function ScoreDoughnut({ value, max = 10, color, icon, title, footnote }) {
    let data;
    let backgroundColor;
    if (value === null || Number.isNaN(Number(value))) {
        data = [0, 1];
        backgroundColor = [DOUGHNUT_EMPTY, DOUGHNUT_EMPTY];
    } else {
        const v = Math.min(Math.max(Number(value), 0), max);
        data = [v, Math.max(max - v, 0)];
        backgroundColor = [color, DOUGHNUT_TRACK];
    }
    const chartData = {
        labels: ['Valor', 'Restante'],
        datasets: [{ data, backgroundColor, borderWidth: 0, hoverOffset: 0 }],
    };
    const display = value !== null && !Number.isNaN(Number(value)) ? Number(value).toFixed(1) : '—';
    return (
        <div className="card border-clean bg-light h-100">
            <div className="card-body text-center d-flex flex-column align-items-center">
                <div className="mb-2">
                    <FontAwesomeIcon icon={icon} className="fa-lg" style={{ color }} />
                    <small className="d-block text-muted mt-1">{title}</small>
                </div>
                <div className="position-relative w-100" style={{ height: 148, maxWidth: 168 }}>
                    <Doughnut data={chartData} options={indicatorDoughnutOptions} />
                    <div
                        className="position-absolute top-50 start-50 translate-middle text-center pointer-events-none"
                        style={{ marginTop: -4 }}
                    >
                        <span className="h3 mb-0 d-block" style={{ color }}>
                            {display}
                        </span>
                    </div>
                </div>
                {footnote ? <small className="text-muted mt-2">{footnote}</small> : null}
            </div>
        </div>
    );
}

/** Taxa de resposta: preenchido vs não respondeu (entre elegíveis). */
function ResponseRateDoughnut({ rate, responded, totalEligible }) {
    const r = rate !== null && !Number.isNaN(Number(rate)) ? Math.min(Math.max(Number(rate), 0), 100) : null;
    let data;
    let backgroundColor;
    if (totalEligible === 0 || r === null) {
        data = [0, 1];
        backgroundColor = [DOUGHNUT_EMPTY, DOUGHNUT_EMPTY];
    } else {
        data = [r, 100 - r];
        backgroundColor = ['#51A8B1', DOUGHNUT_TRACK];
    }
    const chartData = {
        labels: ['Resposta', 'Sem resposta'],
        datasets: [{ data, backgroundColor, borderWidth: 0, hoverOffset: 0 }],
    };
    const display = r !== null ? `${r.toFixed(1)}%` : '—';
    return (
        <div className="card border-clean bg-light h-100">
            <div className="card-body text-center d-flex flex-column align-items-center">
                <div className="mb-2">
                    <small className="d-block text-muted mt-1">Taxa de Resposta</small>
                </div>
                <div className="position-relative w-100" style={{ height: 148, maxWidth: 168 }}>
                    <Doughnut data={chartData} options={indicatorDoughnutOptions} />
                    <div
                        className="position-absolute top-50 start-50 translate-middle text-center pointer-events-none"
                        style={{ marginTop: -4 }}
                    >
                        <span className="h3 mb-0 d-block text-green">{display}</span>
                    </div>
                </div>
                <small className="text-muted mt-2">
                    {responded !== null && totalEligible !== null ? `${responded}/${totalEligible} respostas` : ''}
                </small>
            </div>
        </div>
    );
}

const NPS_COLORS = [
    '#ef4444','#f97316','#f97316','#eab308','#eab308',
    '#eab308','#eab308','#22c55e','#22c55e','#16a34a','#15803d',
];

const LIKERT_COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#16a34a'];

function NpsChart({ distribution }) {
    const labels = ['0','1','2','3','4','5','6','7','8','9','10'];
    const data = {
        labels,
        datasets: [{
            label: 'Respostas',
            data: labels.map(l => distribution?.[l] || 0),
            backgroundColor: NPS_COLORS,
            borderRadius: 4,
        }],
    };
    return (
        <Bar
            data={data}
            options={{
                responsive: true,
                plugins: { legend: { display: false } },
                scales: {
                    x: { grid: { display: false } },
                    y: {
                        beginAtZero: true,
                        ticks: { display: false },
                        grid: { display: false },
                    },
                },
            }}
            height={120}
        />
    );
}

function LikertChart({ distribution, options }) {
    const labels = options
        ? ['1','2','3','4','5'].map(k => options[k] || k)
        : ['Discordo totalmente','Discordo','Neutro','Concordo','Concordo totalmente'];
    const data = {
        labels,
        datasets: [{
            label: 'Respostas',
            data: ['1','2','3','4','5'].map(k => distribution?.[k] || 0),
            backgroundColor: LIKERT_COLORS,
            borderRadius: 4,
        }],
    };
    return (
        <Bar
            data={data}
            options={{
                responsive: true,
                plugins: { legend: { display: false } },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: { display: false },
                        grid: { display: false },
                    },
                    x: {
                        ticks: { maxRotation: 20, font: { size: 11 } },
                        grid: { display: false },
                    },
                },
            }}
            height={130}
        />
    );
}

export default function SurveyDashboard() {
    const { id } = useParams();
    const location = useLocation();
    const toast = React.useRef(null);
    const companyId = localStorage.getItem('companyId');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(() => location.state?.activeTab || 'indicators');
    const [departments, setDepartments] = useState([]);
    const [suborgs, setSuborgs] = useState([]);
    const [departmentId, setDepartmentId] = useState(null);
    const [suborgId, setSuborgId] = useState(null);

    useEffect(() => {
        companyService.listDepartmentSimple().then(res => {
            setDepartments([{ title: 'Todos os departamentos', value: null }, ...res.map(d => ({ title: d.title, value: d.id }))]);
        }).catch(() => {});
        companyService.autoCompleteSubCompany('').then(res => {
            setSuborgs([{ title: 'Todas as suborganizações', value: null }, ...res.map(s => ({ title: s.title, value: s.id }))]);
        }).catch(() => {});
    }, [companyId]);

    useEffect(() => {
        if (!companyId || !id) return;
        setLoading(true);
        const payload = { company_id: companyId, survey_id: id };
        if (departmentId) payload.department_id = departmentId;
        if (suborgId) payload.suborganization_id = suborgId;
        surveyService.getDashboardStats(payload)
            .then(r => setStats(r))
            .catch(err => {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: err.response?.data?.detail || 'Falha ao carregar' });
            })
            .finally(() => setLoading(false));
    }, [companyId, id, departmentId, suborgId]);

    const headerTitle = stats?.survey_name || 'Dashboard da Pesquisa';
    const sch = stats?.send_schedule;

    const npsQuestions = (stats?.by_question || []).filter(q => q.question_type !== 'LIKERT');
    const likertQuestions = (stats?.by_question || []).filter(q => q.question_type === 'LIKERT');

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid" style={{ paddingBottom: '200px' }}>
                <SurveyPageHeader title={headerTitle} />
                <div className="my-4 mx-5 py-3 rounded-top" style={{ minHeight: '500px' }}>
                    <div className="d-flex align-items-center mb-3">
                        <a href="/survey/list/" className="nav-link text-green fw-bold me-2 p-0">
                            <FontAwesomeIcon icon={solid('arrow-left')} /> Voltar à lista
                        </a>
                    </div>

                    {loading ? (
                        <div className="text-center py-5 text-muted">Carregando...</div>
                    ) : stats ? (
                        <>
                            <ul className="nav nav-tabs px-3">
                                <li className="nav-item">
                                    {activeTab === 'indicators' ? (
                                        <span className="nav-link active">Indicadores</span>
                                    ) : (
                                        <button type="button" className="nav-link text-green border-0 bg-transparent" onClick={() => setActiveTab('indicators')}>
                                            Indicadores
                                        </button>
                                    )}
                                </li>
                                <li className="nav-item">
                                    {activeTab === 'schedule' ? (
                                        <span className="nav-link active">Cronograma de envios</span>
                                    ) : (
                                        <button type="button" className="nav-link text-green border-0 bg-transparent" onClick={() => setActiveTab('schedule')}>
                                            Cronograma de envios
                                        </button>
                                    )}
                                </li>
                                <li className="nav-item">
                                    {activeTab === 'triggers' ? (
                                        <span className="nav-link active">Disparo automático</span>
                                    ) : (
                                        <button type="button" className="nav-link text-green border-0 bg-transparent" onClick={() => setActiveTab('triggers')}>
                                            Disparo automático
                                        </button>
                                    )}
                                </li>
                            </ul>

                            <div className="tab-content px-3 pt-3">
                                {activeTab === 'indicators' && (
                                    <>
                                        {/* Filtros */}
                                        <div className="d-flex flex-wrap gap-3 mb-4 align-items-center">
                                            {departments.length > 1 && (
                                                <Dropdown
                                                    value={departmentId}
                                                    options={departments}
                                                    optionLabel="title"
                                                    optionValue="value"
                                                    onChange={e => setDepartmentId(e.value)}
                                                    placeholder="Filtrar por departamento"
                                                    className="w-auto"
                                                    showClear={!!departmentId}
                                                />
                                            )}
                                            {suborgs.length > 1 && (
                                                <Dropdown
                                                    value={suborgId}
                                                    options={suborgs}
                                                    optionLabel="title"
                                                    optionValue="value"
                                                    onChange={e => setSuborgId(e.value)}
                                                    placeholder="Filtrar por suborganização"
                                                    className="w-auto"
                                                    showClear={!!suborgId}
                                                />
                                            )}
                                        </div>

                                        {/* Cards de resumo */}
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-4">
                                                <ScoreDoughnut
                                                    value={stats.overall_score}
                                                    max={10}
                                                    color="#51A8B1"
                                                    title="Score Geral"
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <ScoreDoughnut
                                                    value={stats.engagement_score}
                                                    max={10}
                                                    color="#0d6efd"
                                                    title="Engajamento"
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <ResponseRateDoughnut
                                                    rate={stats.response_rate}
                                                    responded={stats.responded}
                                                    totalEligible={stats.total_eligible}
                                                />
                                            </div>
                                        </div>

                                        {/* Score por dimensão */}
                                        {stats.by_dimension && Object.keys(stats.by_dimension).length > 0 && (
                                            <div className="bg-white rounded border-clean p-4 mb-4">
                                                <h5 className="mb-3">
                                                    <FontAwesomeIcon icon={solid('layer-group')} className="me-2 text-green" />
                                                    Score por Dimensão
                                                </h5>
                                                <div className="table-responsive">
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th>Dimensão</th>
                                                                <th className="text-center">Score</th>
                                                                <th className="text-center">Respostas</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Object.entries(stats.by_dimension).map(([dimId, dim]) => (
                                                                <tr key={dimId} className="align-middle">
                                                                    <td>{dim.name}</td>
                                                                    <td className="text-center fw-bold">{dim.score?.toFixed(1) ?? '—'}</td>
                                                                    <td className="text-center text-muted">{dim.count}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Perguntas NPS */}
                                        {npsQuestions.length > 0 && (
                                            <div className="mb-4">
                                                <h5 className="mb-3">
                                                    <FontAwesomeIcon icon={solid('sliders')} className="me-2 text-green" />
                                                    Perguntas NPS (0–10)
                                                </h5>
                                                <div className="row g-3">
                                                    {npsQuestions.map(q => (
                                                        <div key={q.id} className="col-md-6">
                                                            <div className="bg-white rounded border-clean p-3 h-100">
                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                    <p className="mb-0 fw-medium small" style={{ maxWidth: '75%' }}>{q.text}</p>
                                                                    <span className="badge bg-light border text-muted ms-2">
                                                                        {q.avg_score !== null ? q.avg_score.toFixed(1) : '—'}
                                                                    </span>
                                                                </div>
                                                                {q.dimension_name && (
                                                                    <span className="badge bg-light border text-muted small mb-2">{q.dimension_name}</span>
                                                                )}
                                                                <NpsChart distribution={q.distribution} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Perguntas Likert */}
                                        {likertQuestions.length > 0 && (
                                            <div className="mb-4" style={{ paddingBottom: '200px' }}>
                                                <h5 className="mb-3">
                                                    <FontAwesomeIcon icon={solid('list-check')} className="me-2 text-green" />
                                                    Perguntas Likert (1–5)
                                                </h5>
                                                <div className="row g-3">
                                                    {likertQuestions.map(q => (
                                                        <div key={q.id} className="col-md-6">
                                                            <div className="bg-white rounded border-clean p-3 h-100">
                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                    <p className="mb-0 fw-medium small" style={{ maxWidth: '75%' }}>{q.text}</p>
                                                                    <span className="badge bg-light border text-muted ms-2">
                                                                        {q.avg_score !== null ? q.avg_score.toFixed(1) : '—'}
                                                                    </span>
                                                                </div>
                                                                {q.dimension_name && (
                                                                    <span className="badge bg-light border text-muted small mb-2">{q.dimension_name}</span>
                                                                )}
                                                                <LikertChart distribution={q.distribution} options={q.likert_options} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'schedule' && (
                                    <div className="bg-white rounded border-clean p-4" style={{ marginBottom: '200px' }}>
                                        {!sch ? (
                                            <p className="text-muted mb-0">
                                                Não há período de início/término definido para esta pesquisa.
                                                Ao ativar, as datas e a frequência passam a gerar o cronograma de notificações.
                                            </p>
                                        ) : (
                                            <>
                                                <div className="row g-2 small text-muted mb-4">
                                                    <div className="col-md-6">
                                                        <strong className="text-default d-block">Período da pesquisa</strong>
                                                        {new Date(sch.start_date).toLocaleString('pt-BR')} —{' '}
                                                        {new Date(sch.end_date).toLocaleString('pt-BR')}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <strong className="text-default d-block">Frequência de envios</strong>
                                                        {sch.frequency_display} (a cada {sch.interval_days} dias)
                                                    </div>
                                                    <div className="col-12 mt-2">
                                                        <span className="me-3"><strong className="text-default">Total de disparos:</strong> {sch.total_slots}</span>
                                                        <span className="me-3"><strong className="text-default">Já passaram:</strong> {sch.past_slots}</span>
                                                        <span><strong className="text-default">Futuros / hoje:</strong> {sch.future_slots}</span>
                                                        {sch.total_slots > sch.slots?.length && (
                                                            <div className="text-warning mt-1">
                                                                Mostrando os primeiros {sch.slots.length} de {sch.total_slots} envios.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <h6 className="mb-3">Datas programadas</h6>
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Tipo</th>
                                                                <th>Data e hora</th>
                                                                <th>Situação</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(sch.slots || []).map((row) => (
                                                                <tr key={row.index}>
                                                                    <td>{row.index + 1}</td>
                                                                    <td>
                                                                        <span className="text-muted">{row.index === 0 ? '1º envio' : 'Lembrete'}</span>
                                                                    </td>
                                                                    <td>{new Date(row.at).toLocaleString('pt-BR')}</td>
                                                                    <td>
                                                                        {row.is_past ? (
                                                                            <Chip label="Realizado" className="bg-secondary text-white border-0" />
                                                                        ) : (
                                                                            <Chip label="Agendado" className="bg-info text-white border-0" />
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'triggers' && (
                                    <SurveyTriggerSection
                                        surveyId={id}
                                        surveyStatus={stats.survey_status}
                                        toastRef={toast}
                                    />
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="alert alert-warning">Nenhum dado disponível.</div>
                    )}
                </div>
            </div>
        </ProtectedRoute>
    );
}
